import React, { useState, useEffect } from 'react';
import mixpanelService from '../services/mixpanelService';
import { SparklesIcon } from '@heroicons/react/24/solid';
import { CalendarIcon, ClockIcon, MusicalNoteIcon } from '@heroicons/react/24/outline';
import { format, subDays, startOfDay } from 'date-fns';
import subscriptionService from '../services/subscriptionService';
import { getUserRole } from '../utils/auth';
import DashboardGate from './DashboardGate';
import RequirementsBanner from './RequirementsBanner';
import MonitorOverview from './MonitorOverview';
import StatSection from './volumeDelta/StatSection';
import VolumeDeltaOverview from './volumeDelta/VolumeDeltaOverview';
import VolumeDeltaHeatmap from './volumeDelta/VolumeDeltaHeatmap';
import VolumeDeltaBreakout from './volumeDelta/VolumeDeltaBreakout';
import LatenessOverview from './lateness/LatenessOverview';
import LatenessHeatmap from './lateness/LatenessHeatmap';
import LatenessBreakout from './lateness/LatenessBreakout';
import ArticulationOverview from './articulation/ArticulationOverview';
import ArticulationHeatmap from './articulation/ArticulationHeatmap';
import ArticulationBreakout from './articulation/ArticulationBreakout';
import apiClient from '../utils/apiClient';
import {
  filterSessionsByDays,
  calculateAverage,
  calculateTrend,
  getLoudestQuietestFingers,
  formatBalance
} from '../utils/volumeDeltaUtils';
import {
  getEarliestLatestFingers,
  calculateSynchronization
} from '../utils/latenessUtils';
import {
  getBestWorstFingers as getArticulationBestWorstFingers
} from '../utils/articulationUtils';

const MonitorMode = ({ user }) => {
  // State
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Controls
  const [timeMode, setTimeMode] = useState('all'); // 'all', '30', '7', 'trend'

  // Breakout panels - separate state for volume delta, lateness, and articulation
  const [selectedVolumeCell, setSelectedVolumeCell] = useState(null);
  const [selectedLatenessCell, setSelectedLatenessCell] = useState(null);
  const [selectedArticulationCell, setSelectedArticulationCell] = useState(null);

  // Calendar data
  const [calendarDays, setCalendarDays] = useState([]);

  // Teacher-specific state
  const [isTeacher, setIsTeacher] = useState(false);
  const [students, setStudents] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState('self');
  const [loadingStudents, setLoadingStudents] = useState(false);

  // Subscription state
  const [canAccess, setCanAccess] = useState(false);
  const [checkingSubscription, setCheckingSubscription] = useState(true);

  // Dashboard unlock state
  const [dashboardAccess, setDashboardAccess] = useState(null);
  const [checkingDashboardAccess, setCheckingDashboardAccess] = useState(true);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);

  // Check user role and subscription on mount
  useEffect(() => {
    checkUserRole();
  }, []);

  // Check subscription when teacher status changes
  useEffect(() => {
    checkSubscription();
  }, [isTeacher, selectedStudent]);

  // Check dashboard access when subscription or student selection changes
  useEffect(() => {
    if (canAccess) {
      checkDashboardAccess();
    }
  }, [canAccess, selectedStudent]);

  // Load data when access is granted (for subscribed users) or when unlocked (for free users)
  // Subscribed users should see data even if requirements aren't met
  useEffect(() => {
    const isSubscribed = subscriptionStatus?.status === 'active' || subscriptionStatus?.status === 'trialing';
    const shouldLoadData = canAccess && dashboardAccess && (isSubscribed || dashboardAccess.unlocked);

    if (shouldLoadData) {
      loadData();
    }
  }, [canAccess, dashboardAccess, selectedStudent, subscriptionStatus]);

  const checkUserRole = async () => {
    const role = await getUserRole();
    setIsTeacher(role === 'teacher');
    if (role === 'teacher') {
      loadStudents();
    }
  };

  const loadStudents = async () => {
    setLoadingStudents(true);
    try {
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
      const response = await apiClient.fetch(`${API_BASE_URL}/studio/students`, {
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        // Filter only accepted students and format the data
        const acceptedStudents = (data || []).filter(s => s.status === 'accepted').map(student => ({
          id: student.id,
          full_name: `${student.first_name} ${student.last_name}`.trim() || 'Student',
          email: student.email
        }));
        setStudents(acceptedStudents);
      }
    } catch (error) {
      console.error('Error loading students:', error);
    } finally {
      setLoadingStudents(false);
    }
  };

  const checkSubscription = async () => {
    try {
      const info = await subscriptionService.getSubscriptionInfo();
      setSubscriptionStatus(info);
      // Teachers can view their students even on free tier
      // Students and teachers viewing their own data need subscription
      const hasAccess = info.status === 'active' || info.status === 'trialing' ||
                        info.can_access_monitor ||
                        (isTeacher && selectedStudent !== 'self' && info.can_view_students);
      setCanAccess(hasAccess);
    } catch (err) {
      console.error('Error checking subscription:', err);
      setCanAccess(false);
    } finally {
      setCheckingSubscription(false);
    }
  };

  const checkDashboardAccess = async () => {
    // Only check dashboard access when viewing own data
    if (selectedStudent !== 'self') {
      setDashboardAccess({ unlocked: true });
      setCheckingDashboardAccess(false);
      return;
    }

    setCheckingDashboardAccess(true);
    try {
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
      const response = await apiClient.fetch(`${API_BASE_URL}/monitor/dashboard-access`, {
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setDashboardAccess(data);
      } else {
        console.error('Dashboard access check failed:', response.status);
        // On error, default to locked with zero progress
        setDashboardAccess({
          unlocked: false,
          requirements: {
            total_sessions: { required: 1000, current: 0, met: false, remaining: 1000 },
            favorite_exercise: { required: 5, current: 0, met: false, remaining: 5, exercise_name: null },
            streak: { required: 3, current: 0, longest: 0, met: false }
          },
          stats: {
            total_sessions: 0,
            top_exercise: null,
            top_exercise_count: 0,
            current_streak: 0,
            longest_streak: 0
          }
        });
      }
    } catch (err) {
      console.error('Error checking dashboard access:', err);
      // On error, default to locked with zero progress
      setDashboardAccess({
        unlocked: false,
        requirements: {
          total_sessions: { required: 1000, current: 0, met: false, remaining: 1000 },
          favorite_exercise: { required: 5, current: 0, met: false, remaining: 5, exercise_name: null },
          streak: { required: 3, current: 0, longest: 0, met: false }
        },
        stats: {
          total_sessions: 0,
          top_exercise: null,
          top_exercise_count: 0,
          current_streak: 0,
          longest_streak: 0
        }
      });
    } finally {
      setCheckingDashboardAccess(false);
    }
  };

  const loadData = async () => {
    setLoading(true);
    setError(null);

    try {
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
      // Build URL with optional student_id parameter
      let url = `${API_BASE_URL}/monitor/volume-delta`;
      if (selectedStudent !== 'self') {
        url += `?student_id=${selectedStudent}`;
      }

      const response = await apiClient.fetch(url, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const text = await response.text();
        console.error('Error response:', text);
        throw new Error(`Failed to fetch data: ${response.statusText}`);
      }

      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const text = await response.text();
        console.error('Non-JSON response:', text);
        throw new Error('Server returned non-JSON response. Check backend logs.');
      }

      const data = await response.json();
      const loadedSessions = data.sessions || [];
      setSessions(loadedSessions);

      // Generate calendar data
      generateCalendarData(loadedSessions);
      mixpanelService.track('Monitor Data Loaded', {
        monitor_variant: 'default',
        viewing_own_data: selectedStudent === 'self',
        sessions_count: loadedSessions.length
      });
    } catch (err) {
      console.error('Error loading volume delta data:', err);
      setError(err.message || 'Failed to load data');
      mixpanelService.trackFailure('monitor_data_load', err, {
        monitor_variant: 'default',
        viewing_own_data: selectedStudent === 'self'
      });
    } finally {
      setLoading(false);
    }
  };

  const generateCalendarData = (rawSessions) => {
    const calendar = {};

    // Initialize last 7 days
    for (let i = 6; i >= 0; i--) {
      const date = startOfDay(subDays(new Date(), i));
      const dateKey = format(date, 'yyyy-MM-dd');
      calendar[dateKey] = {
        date,
        dateStr: format(date, 'MMM d'),
        dayName: format(date, 'EEE'),
        sessions: [],
        totalPlayTime: 0,
        sessionDuration: 0,
        exerciseCount: 0,
        exerciseCounts: {}
      };
    }

    // Group sessions by day
    rawSessions.forEach(session => {
      const sessionDate = new Date(session.start_timestamp);
      const dateKey = format(startOfDay(sessionDate), 'yyyy-MM-dd');

      if (calendar[dateKey]) {
        calendar[dateKey].sessions.push(session);
        calendar[dateKey].totalPlayTime += session.play_time_elapsed || 0;

        // Count each exercise
        const exerciseName = session.exercise_name || 'Unknown';
        calendar[dateKey].exerciseCounts[exerciseName] =
          (calendar[dateKey].exerciseCounts[exerciseName] || 0) + 1;
      }
    });

    // Calculate derived metrics for each day
    Object.values(calendar).forEach(day => {
      if (day.sessions.length > 0) {
        // Sort sessions by time
        day.sessions.sort((a, b) =>
          new Date(a.start_timestamp) - new Date(b.start_timestamp)
        );

        // Calculate session duration (first to last)
        const firstStart = new Date(day.sessions[0].start_timestamp);
        const lastEnd = day.sessions[day.sessions.length - 1].end_timestamp
          ? new Date(day.sessions[day.sessions.length - 1].end_timestamp)
          : new Date(day.sessions[day.sessions.length - 1].start_timestamp);

        day.sessionDuration = (lastEnd - firstStart) / 1000; // in seconds
        day.exerciseCount = Object.keys(day.exerciseCounts).length;
      }
    });

    // Convert to array and sort by date
    const calendarArray = Object.values(calendar).sort((a, b) => a.date - b.date);
    setCalendarDays(calendarArray);
  };

  const handleVolumeCellClick = (selection) => {
    setSelectedVolumeCell(selection);
    mixpanelService.track('Monitor Detail Opened', {
      analysis_type: 'volume_delta',
      exercise_name: selection?.exercise || selection?.exercise_name || 'all'
    });
  };

  const handleCloseVolumeBreakout = () => {
    setSelectedVolumeCell(null);
  };

  const handleLatenessCellClick = (selection) => {
    setSelectedLatenessCell(selection);
    mixpanelService.track('Monitor Detail Opened', {
      analysis_type: 'lateness',
      exercise_name: selection?.exercise || selection?.exercise_name || 'all'
    });
  };

  const handleCloseLatenessBreakout = () => {
    setSelectedLatenessCell(null);
  };

  const handleArticulationCellClick = (selection) => {
    setSelectedArticulationCell(selection);
    mixpanelService.track('Monitor Detail Opened', {
      analysis_type: 'articulation',
      exercise_name: selection?.exercise || selection?.exercise_name || 'all'
    });
  };

  const handleCloseArticulationBreakout = () => {
    setSelectedArticulationCell(null);
  };

  // Filter sessions based on controls
  const filteredSessions = React.useMemo(() => {
    let filtered = sessions;

    // Filter by time
    filtered = filterSessionsByDays(filtered, timeMode);

    return filtered;
  }, [sessions, timeMode]);

  // Calculate collapsed content for Volume Delta
  const volumeDeltaCollapsedContent = React.useMemo(() => {
    if (filteredSessions.length === 0) return null;

    const balanceValue = timeMode === 'trend'
      ? calculateTrend(filteredSessions, 'balance')
      : { simple: calculateAverage(filteredSessions, 'balance') };

    const leftFingers = getLoudestQuietestFingers(filteredSessions, 'left');
    const rightFingers = getLoudestQuietestFingers(filteredSessions, 'right');

    // Find overall loudest and quietest across both hands
    const loudestCandidates = [
      leftFingers.loudest ? { ...leftFingers.loudest, hand: 'L', color: '#9333ea' } : null,
      rightFingers.loudest ? { ...rightFingers.loudest, hand: 'R', color: '#ea580c' } : null
    ].filter(Boolean);

    const quietestCandidates = [
      leftFingers.quietest ? { ...leftFingers.quietest, hand: 'L', color: '#9333ea' } : null,
      rightFingers.quietest ? { ...rightFingers.quietest, hand: 'R', color: '#ea580c' } : null
    ].filter(Boolean);

    const overallLoudest = loudestCandidates.length > 0
      ? loudestCandidates.reduce((a, b) => a.avg > b.avg ? a : b)
      : null;

    const overallQuietest = quietestCandidates.length > 0
      ? quietestCandidates.reduce((a, b) => a.avg < b.avg ? a : b)
      : null;

    return (
      <div className="flex items-center gap-6 text-xs">
        {/* Balance */}
        <div className="flex items-center gap-2">
          <span className="font-semibold text-gray-600">Balance:</span>
          {timeMode === 'trend' ? (
            <span className={`font-medium ${balanceValue.difference > 0 ? 'text-green-600' : 'text-blue-600'}`}>
              {balanceValue.last5?.toFixed(1) || 'N/A'} → {balanceValue.last30?.toFixed(1) || 'N/A'}
              (Δ {balanceValue.difference?.toFixed(1) || 'N/A'})
            </span>
          ) : (
            <span className="font-medium">{formatBalance(balanceValue.simple)}</span>
          )}
        </div>

        {/* Loudest/Quietest */}
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <span className="font-semibold text-gray-600">Loud:</span>
            {overallLoudest ? (
              <span style={{ color: overallLoudest.color }}>
                {overallLoudest.hand}{overallLoudest.finger}
              </span>
            ) : (
              <span>-</span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <span className="font-semibold text-gray-600">Quiet:</span>
            {overallQuietest ? (
              <span style={{ color: overallQuietest.color }}>
                {overallQuietest.hand}{overallQuietest.finger}
              </span>
            ) : (
              <span>-</span>
            )}
          </div>
        </div>

        {/* Trend indicator if in trend mode */}
        {timeMode === 'trend' && (
          <div className="ml-auto text-[10px] text-gray-500">
            Last 5 vs Last 30 days
          </div>
        )}
      </div>
    );
  }, [filteredSessions, timeMode]);

  // Calculate collapsed content for Lateness
  const latenessCollapsedContent = React.useMemo(() => {
    if (filteredSessions.length === 0) return null;

    const syncValue = timeMode === 'trend'
      ? calculateTrend(filteredSessions, 'synchronization')
      : { simple: calculateSynchronization(filteredSessions) };

    const leftFingers = getEarliestLatestFingers(filteredSessions, 'left');
    const rightFingers = getEarliestLatestFingers(filteredSessions, 'right');

    return (
      <div className="flex items-center gap-6 text-xs">
        {/* Synchronization */}
        <div className="flex items-center gap-2">
          <span className="font-semibold text-gray-600">Sync:</span>
          {timeMode === 'trend' ? (
            <span className={`font-medium ${syncValue.difference > 0 ? 'text-orange-600' : 'text-purple-600'}`}>
              {syncValue.last5?.toFixed(1) || 'N/A'}ms → {syncValue.last30?.toFixed(1) || 'N/A'}ms
              (Δ {syncValue.difference?.toFixed(1) || 'N/A'}ms)
            </span>
          ) : (
            <span className="font-medium">
              {syncValue.simple != null ? `${syncValue.simple.toFixed(1)}ms` : 'N/A'}
            </span>
          )}
        </div>

        {/* Earliest/Latest */}
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <span className="font-semibold text-gray-600">Early:</span>
            <span style={{ color: '#9333ea' }}>L{leftFingers.earliest?.finger || '-'}</span>
            <span className="text-gray-400">/</span>
            <span style={{ color: '#ea580c' }}>R{rightFingers.earliest?.finger || '-'}</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="font-semibold text-gray-600">Late:</span>
            <span style={{ color: '#9333ea' }}>L{leftFingers.latest?.finger || '-'}</span>
            <span className="text-gray-400">/</span>
            <span style={{ color: '#ea580c' }}>R{rightFingers.latest?.finger || '-'}</span>
          </div>
        </div>

        {/* Trend indicator if in trend mode */}
        {timeMode === 'trend' && (
          <div className="ml-auto text-[10px] text-gray-500">
            Last 5 vs Last 30 days
          </div>
        )}
      </div>
    );
  }, [filteredSessions, timeMode]);

  // Calculate collapsed content for Articulation
  const articulationCollapsedContent = React.useMemo(() => {
    if (filteredSessions.length === 0) return null;

    const overallAccuracy = timeMode === 'trend'
      ? calculateTrend(filteredSessions, 'articulation_accuracy')
      : { simple: calculateAverage(filteredSessions, 'articulation_accuracy') };

    const leftFingers = getArticulationBestWorstFingers(filteredSessions, 'left');
    const rightFingers = getArticulationBestWorstFingers(filteredSessions, 'right');

    return (
      <div className="flex items-center gap-6 text-xs">
        {/* Overall Accuracy */}
        <div className="flex items-center gap-2">
          <span className="font-semibold text-gray-600">Overall:</span>
          {timeMode === 'trend' ? (
            <span className={`font-medium ${overallAccuracy.difference > 0 ? 'text-green-600' : 'text-red-600'}`}>
              {overallAccuracy.last5?.toFixed(1) || 'N/A'}% → {overallAccuracy.last30?.toFixed(1) || 'N/A'}%
              (Δ {overallAccuracy.difference?.toFixed(1) || 'N/A'}%)
            </span>
          ) : (
            <span className="font-medium">
              {overallAccuracy.simple != null ? `${overallAccuracy.simple.toFixed(1)}%` : 'N/A'}
            </span>
          )}
        </div>

        {/* Best/Worst */}
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <span className="font-semibold text-gray-600">Best:</span>
            <span style={{ color: '#9333ea' }}>L{leftFingers.best?.finger || '-'}</span>
            <span className="text-gray-400">/</span>
            <span style={{ color: '#ea580c' }}>R{rightFingers.best?.finger || '-'}</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="font-semibold text-gray-600">Worst:</span>
            <span style={{ color: '#9333ea' }}>L{leftFingers.worst?.finger || '-'}</span>
            <span className="text-gray-400">/</span>
            <span style={{ color: '#ea580c' }}>R{rightFingers.worst?.finger || '-'}</span>
          </div>
        </div>

        {/* Trend indicator if in trend mode */}
        {timeMode === 'trend' && (
          <div className="ml-auto text-[10px] text-gray-500">
            Last 5 vs Last 30 days
          </div>
        )}
      </div>
    );
  }, [filteredSessions, timeMode]);

  // Subscription check UI
  if (checkingSubscription) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Checking subscription...</p>
        </div>
      </div>
    );
  }

  if (!canAccess) {
    return (
      <div className="flex items-center justify-center h-full p-4">
        <div className="max-w-md text-center p-8 bg-white rounded-xl shadow-lg border border-gray-200">
          <SparklesIcon className="h-16 w-16 text-purple-600 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-3">
            Practice Monitor
          </h2>
          <p className="text-gray-600 mb-6">
            This feature requires an active subscription. Subscribe to unlock advanced analytics and insights.
          </p>
          <button
            onClick={async () => {
              try {
                await subscriptionService.createCheckoutSessionForPeriod('monthly');
              } catch (error) {
                console.error('Error creating checkout session:', error);
                alert('Failed to start checkout. Please try again.');
              }
            }}
            className="px-4 py-3 bg-purple-600 text-white rounded-lg font-semibold hover:bg-purple-700 transition-colors"
          >
            Subscribe Now
          </button>
        </div>
      </div>
    );
  }

  // Check dashboard unlock status (only for viewing own data)
  if (checkingDashboardAccess) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Checking dashboard access...</p>
        </div>
      </div>
    );
  }

  // Determine subscription status
  const isSubscribed = subscriptionStatus?.status === 'active' || subscriptionStatus?.status === 'trialing';
  const isUnlocked = dashboardAccess?.unlocked;

  // Gate logic for viewing own data:
  // 1. Unsubscribed + Not unlocked: Show gate
  // 2. Subscribed + Not unlocked: Show dashboard with banner
  // 3. Unsubscribed + Unlocked: Show gate with celebration
  // 4. Subscribed + Unlocked: Show normal dashboard (no banner)

  const shouldShowGate = selectedStudent === 'self' && !isSubscribed && dashboardAccess;
  const shouldShowBanner = selectedStudent === 'self' && isSubscribed && dashboardAccess && !isUnlocked;

  if (shouldShowGate) {
    return <DashboardGate accessData={dashboardAccess} isSubscribed={false} />;
  }

  return (
    <div className="h-full flex flex-col bg-gray-50">
      {/* Header with controls */}
      <div className="bg-white border-b shadow-sm p-4">
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-2xl font-bold text-gray-900 flex items-center">
            <SparklesIcon className="h-6 w-6 text-purple-600 mr-2" />
            Practice Analytics
          </h1>
          <button
            onClick={loadData}
            disabled={loading}
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:bg-gray-400 transition-colors"
          >
            {loading ? 'Loading...' : 'Refresh'}
          </button>
        </div>

        {/* Controls */}
        <div className="flex flex-wrap gap-6">
          {/* Student Selector (Teachers only) */}
          {isTeacher && (
            <div className="flex items-center space-x-3">
              <label className="text-sm font-semibold text-gray-700">View Data For:</label>
              <select
                value={selectedStudent}
                onChange={(e) => setSelectedStudent(e.target.value)}
                disabled={loadingStudents}
                className="px-3 py-1.5 rounded-lg border border-gray-300 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white"
              >
                <option value="self">My Practice</option>
                {students.map(student => (
                  <option key={student.id} value={student.id}>
                    {student.full_name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Time Range Selector */}
          <div className="flex items-center space-x-3">
            <label className="text-sm font-semibold text-gray-700">Time Range:</label>
            <div className="flex bg-gray-100 rounded-lg p-1">
              {[
                { value: 'all', label: 'All' },
                { value: '30', label: '30d' },
                { value: '7', label: '7d' },
                { value: 'trend', label: 'Trend' }
              ].map(option => (
                <button
                  key={option.value}
                  onClick={() => setTimeMode(option.value)}
                  className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                    timeMode === option.value
                      ? 'bg-purple-600 text-white'
                      : 'text-gray-700 hover:text-gray-900'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="m-4 p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-800 text-sm">{error}</p>
        </div>
      )}

      {/* Main Content */}
      <div className="flex-1 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
              <p className="text-gray-600">Loading data...</p>
            </div>
          </div>
        ) : sessions.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center p-8">
              <p className="text-gray-600 text-lg mb-2">No practice sessions found</p>
              <p className="text-gray-500 text-sm">Start practicing to see your volume delta analytics</p>
            </div>
          </div>
        ) : (
          <div className="h-full overflow-y-auto p-4 bg-gray-50">
            <div className="space-y-4">
              {/* Show requirements banner for subscribed users who haven't met requirements */}
              {shouldShowBanner && <RequirementsBanner accessData={dashboardAccess} />}

              {/* Practice Overview Section */}
              <MonitorOverview sessions={sessions} />

              {/* Volume Delta Section */}
              <StatSection
                title="Volume"
                defaultExpanded={false}
                collapsedContent={volumeDeltaCollapsedContent}
              >
                <div className="space-y-4">
                  {/* Overview */}
                  <VolumeDeltaOverview
                    sessions={sessions}
                    filteredSessions={filteredSessions}
                    timeMode={timeMode}
                    onCellClick={handleVolumeCellClick}
                  />

                  {/* Heatmap - Always visible */}
                  <VolumeDeltaHeatmap
                    sessions={filteredSessions}
                    timeMode={timeMode}
                    onCellClick={handleVolumeCellClick}
                  />

                  {/* Detailed Stats - Appears below when cell is clicked */}
                  {selectedVolumeCell && (
                    <div className="mt-4">
                      <VolumeDeltaBreakout
                        sessions={filteredSessions}
                        selection={selectedVolumeCell}
                        onClose={handleCloseVolumeBreakout}
                      />
                    </div>
                  )}
                </div>
              </StatSection>

              {/* Timing Section */}
              <StatSection
                title="Timing"
                defaultExpanded={false}
                collapsedContent={latenessCollapsedContent}
              >
                <div className="space-y-4">
                  {/* Overview */}
                  <LatenessOverview
                    sessions={sessions}
                    filteredSessions={filteredSessions}
                    timeMode={timeMode}
                  />

                  {/* Heatmap - Always visible */}
                  <LatenessHeatmap
                    sessions={filteredSessions}
                    timeMode={timeMode}
                    onCellClick={handleLatenessCellClick}
                  />

                  {/* Detailed Stats - Appears below when cell is clicked */}
                  {selectedLatenessCell && (
                    <div className="mt-4">
                      <LatenessBreakout
                        sessions={filteredSessions}
                        selection={selectedLatenessCell}
                        onClose={handleCloseLatenessBreakout}
                      />
                    </div>
                  )}
                </div>
              </StatSection>

              {/* Articulation Section */}
              <StatSection
                title="Articulation"
                defaultExpanded={false}
                collapsedContent={articulationCollapsedContent}
              >
                <div className="space-y-4">
                  {/* Overview */}
                  <ArticulationOverview
                    sessions={sessions}
                    filteredSessions={filteredSessions}
                    timeMode={timeMode}
                  />

                  {/* Heatmap - Always visible */}
                  <ArticulationHeatmap
                    sessions={filteredSessions}
                    timeMode={timeMode}
                    onCellClick={handleArticulationCellClick}
                  />

                  {/* Detailed Stats - Appears below when cell is clicked */}
                  {selectedArticulationCell && (
                    <div className="mt-4">
                      <ArticulationBreakout
                        sessions={filteredSessions}
                        selection={selectedArticulationCell}
                        onClose={handleCloseArticulationBreakout}
                      />
                    </div>
                  )}
                </div>
              </StatSection>

              {/* Last 7 Days Practice Calendar */}
              <div className="mt-8 pt-8 border-t bg-white rounded-lg p-4">
                <h3 className="text-lg font-semibold mb-3 flex items-center">
                  <CalendarIcon className="h-5 w-5 mr-2" />
                  Last 7 Days Practice Calendar
                </h3>
                <div className="grid grid-cols-7 gap-2">
                  {calendarDays.map((day, index) => (
                    <div
                      key={index}
                      className={`border rounded-lg p-3 ${
                        day.sessions.length > 0 ? 'bg-indigo-50 border-indigo-300' : 'bg-gray-50 border-gray-200'
                      }`}
                    >
                      <div className="text-xs font-medium text-gray-600">{day.dayName}</div>
                      <div className="text-lg font-bold mb-2">{day.dateStr}</div>

                      {day.sessions.length > 0 ? (
                        <div className="space-y-2">
                          {/* Minutes Practiced */}
                          <div className="flex items-center text-xs text-gray-600">
                            <ClockIcon className="h-3 w-3 mr-1 flex-shrink-0" />
                            <span className="font-semibold">
                              {Math.round(day.sessionDuration / 60)} min
                            </span>
                          </div>

                          {/* Exercises Played */}
                          <div className="border-t pt-2">
                            <div className="flex items-center text-xs text-gray-600 mb-1">
                              <MusicalNoteIcon className="h-3 w-3 mr-1 flex-shrink-0" />
                              <span className="font-medium">Exercises:</span>
                            </div>
                            <div className="max-h-32 overflow-y-auto text-xs space-y-1">
                              {Object.entries(day.exerciseCounts).map(([exercise, count], i) => (
                                <div key={i} className="text-indigo-700" style={{ lineHeight: '1.3' }}>
                                  <span className="font-semibold">{count}x</span>{' '}
                                  <span className="text-indigo-600">
                                    {exercise.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="text-xs text-gray-400">No practice</div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MonitorMode;
